import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, trackClick } from '@bonusly/analytics';
import { UserAvatar } from '@bonusly/common-user-popover';
import {
	ArrowUpOutline,
	Box,
	Button,
	Flex,
	InputGroup,
	InputRightElement,
	Stack,
	StackProps,
	Text,
	Textarea,
	useBreakpointValue,
	useOutsideClick,
} from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { ReactComponent as AIInputIcon } from '../assets/ai-input-icon.svg';

interface InputProps extends StackProps {
	value?: number | null;
	message?: string;
	onSend?: (message: string) => void;
	onClose?: () => void;
}

export const Input = forwardRef<HTMLTextAreaElement, InputProps>(({ value = 0, message: initialMessage, onSend, onClose, ...props }, ref) => {
	const [message, setMessage] = useState(initialMessage || '');
	const [error, setError] = useState<boolean | null>(null);
	const { t } = useTranslation('one_on_ones', { keyPrefix: 'meeting.check_in' });
	const messages = t('input_messages', { returnObjects: true }) as string[];
	const currentUser = useCurrentUser();
	const inputRef = useRef<HTMLDivElement>(null);

	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

	const handleInput = useCallback(() => {
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
			textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
		}
	}, []);

	const onSubmit = useCallback(() => {
		setError(false);

		onSend?.(message);
		setMessage('');
	}, [message, onSend, setError, setMessage]);

	const onKeyPress = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				onSubmit();
			}
		},
		[onSubmit]
	);

	// Adjust height when message changes or component mounts
	useEffect(() => {
		if (message) {
			// Small timeout to ensure the DOM has updated
			setTimeout(handleInput, 0);
		}
	}, [message, handleInput]);

	useOutsideClick({
		ref: inputRef,
		handler: (e) => {
			if (e.target instanceof HTMLElement && !inputRef.current?.contains(e.target)) {
				onClose?.();
			}
		},
	});

	const onChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setError(null);
			setMessage(e.target.value || '');
		},
		[setError, setMessage]
	);

	const cappedValue = Math.min(Math.max(value || 0, 0), 5) - 1;
	const roundedValue = Math.round(cappedValue);
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const left = `min(max(calc(${2 * cappedValue * 12}% - 210px), 0px), calc(100% - 420px))`;

	return (
		<Stack
			ref={inputRef}
			bg="bg.surface"
			borderColor="border.default"
			borderWidth={1}
			gap={3}
			maxW={420}
			left={isMobile ? '0px' : left}
			py={4}
			px={2}
			rounded="lg"
			shadow="xl"
			transition="left 0.2s ease-in-out"
			w="100%"
			{...props}
		>
			<Flex gap={2}>
				<Flex boxSize="32px" align="center" justify="center">
					<Box as={AIInputIcon} boxSize="32px" />
				</Flex>
				<Text fontWeight="medium" maxW={490} paddingLeft={1}>
					{(cappedValue + 1).toFixed(1)} - {messages[roundedValue]}
				</Text>
			</Flex>

			<Flex gap={2}>
				<UserAvatar width="32px" height="32px" userId={currentUser.id} />
				<Stack flex={1}>
					<InputGroup>
						<Textarea
							ref={textareaRef}
							onKeyPress={onKeyPress}
							onInput={handleInput}
							autoFocus
							borderColor={error ? 'error.500' : 'border.input'}
							isInvalid={!!error}
							onChange={onChange}
							pr={12}
							pl={3}
							placeholder={t('input_placeholder')}
							resize="none"
							value={message}
							maxHeight={60}
							overflowY="auto"
							rows={2}
							minHeight="auto"
							py={2}
							lineHeight="short"
							fontSize="md"
						/>
						<InputRightElement top={1} right={1}>
							<Button
								borderRadius="md"
								colorScheme="primary"
								h={8}
								minW={8}
								w={8}
								onClick={onSubmit}
								size="xs"
								variant="solid"
								{...trackClick(AnalyticsEvent.CheckIns.MeetingCheckInDetailsSendClicked)}
							>
								<ArrowUpOutline h={22} w={22} />
							</Button>
						</InputRightElement>
					</InputGroup>
				</Stack>
			</Flex>
		</Stack>
	);
});

Input.displayName = 'Input';
